import React from 'react';
import { LottoDraw } from '../types';
import { ExternalLink, Calendar, DollarSign, RefreshCw, Hash, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import LottoBall from './LottoBall';

interface LatestResultsProps {
  draw: LottoDraw | null;
  loading: boolean;
  onRetry: () => void;
}

const moneyText = (value?: string | null) => String(value || 'TBA').replace(/^\$\s*/, '');

const LatestResults: React.FC<LatestResultsProps> = ({ draw, loading, onRetry }) => {
  if (loading) {
    return (
      <div className="w-full p-6 rounded-2xl bg-slate-800/50 border border-slate-700 animate-pulse text-center">
        <div className="h-6 bg-slate-700 rounded w-1/3 mx-auto mb-4"></div>
        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="w-10 h-10 rounded-full bg-slate-700"></div>)}
          <div className="w-10 h-10 rounded-full bg-slate-600"></div>
        </div>
        <div className="h-4 bg-slate-700 rounded w-1/4 mx-auto"></div>
        <p className="mt-4 text-slate-400 text-sm">Checking current official NLCB draw and jackpot...</p>
      </div>
    );
  }

  if (!draw) {
    return (
      <div className="w-full p-6 rounded-2xl bg-red-900/20 border border-red-800 text-center">
        <p className="text-red-200 mb-4">Failed to load latest results.</p>
        <button onClick={onRetry} className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg transition flex items-center justify-center gap-2 mx-auto">
          <RefreshCw size={16} /> Retry official check
        </button>
      </div>
    );
  }

  const verifiedLabel = draw.officialVerifiedAt
    ? new Date(draw.officialVerifiedAt).toLocaleString('en-TT', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Port_of_Spain' })
    : null;

  return (
    <div className="w-full p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 shadow-xl print-container">
      <div className="flex flex-col xl:flex-row justify-between items-start gap-5 mb-8 border-b border-slate-700/50 pb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-100">Latest Completed NLCB Draw</h2>
          <div className="flex flex-col gap-1 mt-1">
            <div className="flex items-center gap-2 text-slate-400 text-sm"><Calendar size={14} /><span>{draw.date}</span></div>
            {draw.drawNumber && <div className="flex items-center gap-2 text-slate-500 text-xs font-mono"><Hash size={12} /><span>Draw #{draw.drawNumber}</span></div>}
            {verifiedLabel && <div className="flex items-center gap-2 text-emerald-400/90 text-xs"><ShieldCheck size={13} /><span>Official NLCB verified {verifiedLabel} AST</span></div>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full xl:w-auto">
          <div className="px-4 py-2 bg-slate-900/70 border border-slate-700 rounded-lg">
            <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Latest Draw Jackpot</span>
            <div className="flex items-center gap-1"><DollarSign size={15} className="text-slate-300" /><span className="text-slate-100 font-bold font-mono text-base">{moneyText(draw.jackpot)}</span></div>
          </div>
          <div className="px-4 py-2 bg-green-900/30 border border-green-700/50 rounded-lg">
            <span className="text-[10px] text-green-500 uppercase font-bold tracking-wider">Next Est./Guaranteed Jackpot</span>
            <div className="flex items-center gap-1"><DollarSign size={15} className="text-green-400" /><span className="text-green-400 font-bold font-mono text-base">{moneyText(draw.nextEstimatedJackpot)}</span></div>
          </div>
        </div>
      </div>

      <div className="text-center mb-4"><h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">Winning Numbers</h3></div>
      <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6">
        {draw.numbers.map((num, idx) => (
          <motion.div key={idx} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 20, delay: idx * 0.1 }}>
            <LottoBall number={num} size="lg" />
          </motion.div>
        ))}
        <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 20, delay: draw.numbers.length * 0.1 }} className="relative pb-5">
          <LottoBall number={draw.powerball} powerball size="lg" />
          <span className="absolute -bottom-1 inset-x-0 text-center text-[10px] text-red-400 font-sans tracking-wider uppercase opacity-80">Power</span>
        </motion.div>
      </div>

      <div className="flex justify-center mb-6">
        <div className="px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
          <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Draw Multiplier</span>
          <div className="text-xl font-black font-mono text-amber-300">{draw.multiplier || 'Unavailable'}</div>
        </div>
      </div>

      {draw.sourceUrl && (
        <div className="text-right no-print pt-4 border-t border-slate-700/50">
          <a href={draw.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center justify-end gap-1 transition-colors">
            Source: {draw.sourceTitle} <ExternalLink size={12} />
          </a>
        </div>
      )}
    </div>
  );
};

export default LatestResults;
